import { graphql, Link, useStaticQuery } from "gatsby"
import React from "react"
import styled, { css } from "styled-components"
import BreakpointImage from "../../atoms/breakpoint-image"
import { Block, Spacer } from "../../atoms/layout-primitives"
import { BlogGrid } from "../../atoms/layout-primitives/grid"
import Text from "../../atoms/text"
import VariantContainer from "../../atoms/variant-container"
import ArticleCard, { IArticle } from "../../molecules/article-card"
import { BT } from "../../styles/blog-typography"
import { truncateContent } from "../../util/contentful-data"
import InternalRoutes from "../../util/internal-routes"

const RoundedBreakpointImage = styled(BreakpointImage)`
  border-radius: 4px;
`
interface IEditorsPickProps {
  title?: string
  article: IArticle
}

/**
 * A highlighted article which appears larger on desktop
 * @param article - the article
 */
const EditorsPick = ({
  title: componentTitle = "Editor's Pick",
  article,
}: IEditorsPickProps) => {
  const { title, slug, image, content, category, topic } = article
  const { logo } = useStaticQuery(graphql`
    query {
      logo: file(relativePath: { eq: "6_BLOG/home/blog_logo.svg" }) {
        publicURL
      }
    }
  `)

  return (
    <>
      <Spacer height={{ sm: "16px", md: "24px", lg: "48px" }} />
      <BlogGrid
        variants={[
          css`
            gap: 24px;
          `,
        ]}
      >
        <Block
          display={{ sm: "flex" }}
          col={{ lg: "1/5" }}
          variants={[
            css`
              flex-direction: column;
              justify-content: center;
              align-items: center;
              padding: 32px 24px;
              background-color: var(--slate);
              box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.15);
              border-radius: 4px;
              gap: 24px;
            `,
            css`
              padding: 48px 80px;
            `,
            css`
              padding: 24px;
            `,
          ]}
        >
          <img src={logo.publicURL} />
          <Text
            as="p"
            variants={[
              css`
                ${BT.sm.body`400`};
                color: var(--white);
                font-size: 18px;
                text-align: center;
              `,
            ]}
          >
            An in-depth look at indoor mapping technologies, use cases, customer
            case studies, and how Mappedin powers mapping experiences for
            world-class venues.
          </Text>
        </Block>
        <Block
          display={{ md: "none" }}
          variants={[
            css`
              ::before {
                content: "Editor's Pick";
                position: absolute;
                z-index: 120;
                background-color: rgba(191, 67, 32, 1);
                ${BT.sm.info`500`};
                color: var(--white);
                font-size: 12px;
                text-transform: uppercase;
                letter-spacing: 0.08em;
                padding: 6px 12px;
                border-radius: 4px 2px 2px 2px;
              }
              pointer-events: none;
            `,
          ]}
        >
          <ArticleCard article={article} />
        </Block>
        <Block
          display={{ sm: "none", md: "block" }}
          col={{ lg: "-9/-1" }}
          variants={[
            css`
              box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.15);
              box-sizing: border-box;
              aspect-ratio: 16/9;
              width: 100%;
              position: relative;
            `,
          ]}
        >
          <Link to={InternalRoutes.Blog(category, topic, slug)}>
            <RoundedBreakpointImage
              fluid={image}
              imgStyle={{ objectFit: "cover" }}
            />
            <VariantContainer
              variants={[
                css`
                  position: absolute;
                  bottom: 16px;
                  right: 16px;
                  margin-left: 20%;
                `,
              ]}
            >
              <Text
                as="p"
                variants={[
                  css`
                    background-color: rgba(191, 67, 32, 0.9);
                    ${BT.sm.info`500`};
                    color: var(--white);
                    font-size: 12px;
                    text-transform: uppercase;
                    letter-spacing: 0.08em;
                    padding: 6px 12px;
                    border-radius: 2px;
                    width: max-content;
                    position: absolute;
                    right: 0;
                    bottom: 100%;
                  `,
                ]}
              >
                {componentTitle}
              </Text>
              <Text
                as="h3"
                variants={[
                  css`
                    ${BT.sm.h3`500`};
                    background-color: rgba(40, 40, 40, 0.9);
                    color: var(--white);
                    padding: 16px 24px;
                    border-radius: 2px 0px 0px 0px;
                  `,
                ]}
              >
                {title}
              </Text>
              <Text
                as="p"
                variants={[
                  css`
                    ${BT.sm.body`400`};
                    font-size: 16px;
                    line-height: 140%;
                    background-color: var(--white);
                    padding: 16px 24px;
                    border-radius: 0px 0px 2px 2px;
                  `,
                ]}
              >
                {truncateContent(content || "")}
              </Text>
            </VariantContainer>
          </Link>
        </Block>
      </BlogGrid>
      <Spacer height={{ sm: "48px", md: "64px" }} />
    </>
  )
}

export const EDITORS_PICK_TYPENAME = "ContentfulFeaturedCards"
export default EditorsPick
